class ProductUsps extends HTMLElement {
  #host = null;
  #grid = null;
  #variantsUsps = {};
  #iconSeq = 0;

  connectedCallback() {
    this.#host = this.closest('main-product');
    this.#grid = this.querySelector('[data-usps-grid]');
    this.#variantsUsps = this.#parseVariantsUsps();
    this.#prefetchIcons();
    this.#host?.addEventListener('pdp:variant-update', this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.#host?.removeEventListener('pdp:variant-update', this.#onVariantUpdate);
  }

  #parseVariantsUsps() {
    const node = this.querySelector('[data-variants-usps]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.error('product-usps: variants USPs JSON parse failed', err);
      return {};
    }
  }

  // Only the initially-selected variant's icons are in the DOM; every other variant's
  // icon lives solely as a URL in the JSON, so the first switch to it would wait on a
  // CDN fetch. Warm the HTTP cache with the unique icon URLs at idle so the decode
  // before each swap resolves instantly. Icons are tiny (20px PNGs).
  #prefetchIcons() {
    const urls = new Set();
    Object.values(this.#variantsUsps).forEach((list) => {
      if (!Array.isArray(list)) return;
      list.forEach((u) => {
        const url = typeof u?.icon_url === 'string' ? u.icon_url.trim() : '';
        if (url) urls.add(url);
      });
    });
    if (urls.size === 0) return;

    const warm = () => urls.forEach((url) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    });

    if ('requestIdleCallback' in window) {
      requestIdleCallback(warm, { timeout: 3000 });
    } else {
      setTimeout(warm, 1200);
    }
  }

  #onVariantUpdate = (event) => {
    const variantId = event.detail?.variant?.id;
    if (!variantId || !this.#grid) return;

    const raw = this.#variantsUsps[String(variantId)] || this.#variantsUsps[variantId];
    const items = (Array.isArray(raw) ? raw : [])
      .map((u) => {
        const text = typeof u?.text === 'string' ? u.text : '';
        const iconUrl = typeof u?.icon_url === 'string' ? u.icon_url : '';
        return {
          text: text.trim() !== '' ? text : '',
          iconUrl: iconUrl.trim() !== '' ? iconUrl : '',
        };
      })
      .filter((u) => u.text !== '' || u.iconUrl !== '');

    this.#reconcile(items);
  };

  // Patch the grid in place instead of replacing innerHTML. Unchanged items are never
  // touched (no flash); text updates in place; icons swap only when the URL changes.
  #reconcile(items) {
    const grid = this.#grid;
    if (items.length === 0) {
      // Clear everything (incl. any stray text nodes) so :empty hides the grid.
      grid.replaceChildren();
      return;
    }

    const existing = Array.from(grid.children);
    for (let i = 0; i < items.length; i += 1) {
      if (existing[i]) this.#patchItem(existing[i], items[i]);
      else this.#appendItem(items[i]);
    }
    for (let i = items.length; i < existing.length; i += 1) {
      existing[i].remove();
    }
  }

  #patchItem(node, item) {
    const commitText = () => this.#setText(node, item.text);

    // Icon (rendered before text within the item).
    if (item.iconUrl !== '') {
      // #applyIcon commits the text: immediately when the icon is unchanged or newly
      // added, or together with the decoded image when replacing a live icon — so a row
      // is never painted with the new text beside the previous variant's icon.
      this.#applyIcon(node, item.iconUrl, commitText);
    } else {
      // Bump the sequence so any in-flight icon swap for this row is superseded.
      this.#claimIconSeq(node);
      node.querySelector('.product-usps__icon')?.remove();
      commitText();
    }
  }

  #appendItem(item) {
    const node = document.createElement('div');
    node.className = 'product-usps__item is-usps-enter';
    const commitText = () => this.#setText(node, item.text);

    if (item.iconUrl !== '') {
      // Attach first so the isConnected guard in #applyIcon holds; #applyIcon reserves
      // the icon box up front (no reflow) and commits the text right away since there is
      // no previous icon to mismatch. The bitmap fills the reserved box once decoded.
      this.#grid.appendChild(node);
      this.#applyIcon(node, item.iconUrl, commitText);
    } else {
      commitText();
      this.#grid.appendChild(node);
    }
  }

  #setText(node, text) {
    let textEl = node.querySelector('.product-usps__text');
    if (text !== '') {
      if (!textEl) {
        textEl = document.createElement('div');
        textEl.className = 'product-usps__text u-s4';
        node.appendChild(textEl);
      }
      if (textEl.textContent !== text) textEl.textContent = text;
    } else if (textEl) {
      textEl.remove();
    }
  }

  // Monotonic per-row token: each swap (or icon removal) claims the next value, so an
  // async insert/failure knows if a newer swap has superseded it. Keyed by sequence, not
  // URL, so two swaps to the SAME icon URL with different text still supersede correctly.
  #claimIconSeq(node) {
    this.#iconSeq += 1;
    node.dataset.iconSeq = String(this.#iconSeq);
    return this.#iconSeq;
  }

  // Change or add a row's icon with no blank frame, no reflow and no leftover nodes:
  // reserve the fixed icon box synchronously (so filling it never shifts the text),
  // decode the new image off-DOM, then swap the single <img> once it is ready. When
  // replacing a live icon the old one stays visible until decode resolves and the text
  // is committed together with it; the text is always committed (even on decode failure)
  // so the row never keeps the previous variant's label. Rapid switching resolves to the
  // newest icon; stale decodes are discarded via the per-row sequence token.
  #applyIcon(node, url, onCommit) {
    const wrap0 = node.querySelector('.product-usps__icon');
    const current = wrap0?.querySelector('img');
    // Server-rendered icons carry no dataset.iconUrl; their src (width:96) matches the
    // JSON URL, so fall back to it for the "unchanged icon" comparison.
    const currentUrl = current ? (current.dataset.iconUrl ?? current.getAttribute('src')) : null;
    if (currentUrl === url) {
      // Icon already correct; supersede any pending swap and commit the text now.
      this.#claimIconSeq(node);
      onCommit?.();
      return;
    }

    const seq = this.#claimIconSeq(node);
    const hadLiveIcon = !!current;
    let wrap = wrap0;
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'product-usps__icon';
      node.prepend(wrap);
    }
    // With no previous icon there is nothing to mismatch, so let the text show now.
    if (!hadLiveIcon) onCommit?.();

    const next = new Image();
    next.alt = '';
    next.width = 20;
    next.height = 20;
    next.decoding = 'async';
    next.dataset.iconUrl = url;
    next.src = url;

    const isCurrent = () => node.dataset.iconSeq === String(seq) && node.isConnected;

    const insert = () => {
      if (!isCurrent()) return;
      let w = node.querySelector('.product-usps__icon');
      if (!w) {
        w = document.createElement('div');
        w.className = 'product-usps__icon';
        node.prepend(w);
      }
      const live = w.querySelector('img');
      if (live) live.replaceWith(next);
      else w.appendChild(next);
      // Swap the text in the same frame as the icon it belongs to.
      if (hadLiveIcon) onCommit?.();
    };

    const onFail = () => {
      if (!isCurrent()) return;
      // The icon identifies the feature, so never leave the previous variant's icon
      // beside the new text. Drop the icon (a live stale one or the empty reserved box)
      // and show the selected variant's text alone.
      node.querySelector('.product-usps__icon')?.remove();
      onCommit?.();
    };

    if (typeof next.decode === 'function') {
      next.decode().then(insert, onFail);
    } else {
      next.addEventListener('load', insert, { once: true });
      next.addEventListener('error', onFail, { once: true });
    }
  }
}

if (!customElements.get('product-usps')) {
  customElements.define('product-usps', ProductUsps);
}
